import React from "react";
import { Card } from "react-bootstrap";
import { useHistory } from "react-router-dom";

import { NoRecordCard } from "../../../common/components";
import { Table } from "../../../common/components/core";
import { useSelector } from "../../../redux/hooks";
import {
    currentRolesSelector,
    currentUserEmailSelector,
} from "../../../redux/slices/auth/selectors";
import { BesuClaimState } from "../../../types/claim";
import { InvoiceStateWithPaymentInfo } from "../../../types/invoice";
import { BesuProjectState } from "../../../types/project";
import { formatToDate, shortenUUID } from "../../../utils/common";
import {
    compareInvoiceDateDescAndActionFirst,
    getNestedInvoiceStatus,
    hasNestedInvoiceAction,
} from "../../../utils/invoice";

import InvoicePaymentStatusBadge from "./InvoicePaymentStatusBadge";

type InvoiceListProps = {
    project: BesuProjectState;
    claim: BesuClaimState;
    invoices: InvoiceStateWithPaymentInfo[];
};

const InvoiceList = ({
    project,
    claim,
    invoices,
}: InvoiceListProps): JSX.Element => {
    const history = useHistory();
    const currentRoles = useSelector(currentRolesSelector);
    const currentUserEmail = useSelector(currentUserEmailSelector);
    const sortedInvoices = [...invoices].sort(
        compareInvoiceDateDescAndActionFirst(currentRoles, currentUserEmail),
    );
    return invoices.length === 0 ? (
        <NoRecordCard />
    ) : (
        <Card className="mt-3 border-0">
            <Card.Body className="px-0 py-0">
                <table className={'custom-table'}>
                    <thead>
                        <tr>
                            <Table.HeaderCell width={40}>
                                CTA
                            </Table.HeaderCell>
                            <Table.HeaderCell width={160}>
                                Invoice ID
                            </Table.HeaderCell>
                            <Table.HeaderCell width={160}>
                                Creation Date
                            </Table.HeaderCell>
                            <Table.HeaderCell width={160} className="text-end">
                                Invoice Amount
                            </Table.HeaderCell>
                            <Table.HeaderCell width={160}>
                                Status
                            </Table.HeaderCell>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedInvoices.map((nestedInvoice) => {
                            const { invoiceState: invoice, paymentInfo } =
                                nestedInvoice;
                            const hasAction = hasNestedInvoiceAction(
                                currentRoles,
                                getNestedInvoiceStatus(nestedInvoice),
                                currentUserEmail,
                            );
                            return (
                                <Table.BodyRow
                                    key={invoice.linearId.id}
                                    hasAction={hasAction}
                                    onClick={() =>
                                        history.push(
                                            `/project/${project.referenceId}/claim/${claim.referenceId}/invoice/${invoice.linearId.id}`,
                                        )
                                    }
                                    onKeyUp={(event:KeyboardEvent) =>
                                        {(event.keyCode === 13 || event.keyCode === 32) &&
                                            history.push(
                                                `/project/${project.referenceId}/claim/${claim.referenceId}/invoice/${invoice.linearId.id}`,
                                            )
                                        }
                                    }
                                >
                                    <td className="cta-cell">
                                        {hasAction && <span className="cta" title="action required" aria-label="action required"/>}
                                    </td>
                                    <td>{shortenUUID(invoice.linearId.id)}</td>
                                    <td>{formatToDate(invoice.createdAt)}</td>
                                    <td className="text-end">
                                        {invoice.props.amount}
                                    </td>
                                    <td className="text-end">
                                        <InvoicePaymentStatusBadge
                                            roles={currentRoles}
                                            currentUserEmail={currentUserEmail}
                                            invoiceStatus={invoice.status}
                                            paymentOrderStatus={
                                                paymentInfo?.paymentOrderStatus
                                            }
                                            paymentStatus={
                                                paymentInfo?.paymentStatus
                                            }
                                            paymentOrderApprovers={
                                                paymentInfo?.paymentOrderApprovers
                                            }
                                            isListView={true}
                                        />
                                    </td>
                                </Table.BodyRow>
                            );
                        })}
                    </tbody>
                </table>
            </Card.Body>
        </Card>
    );

};

export default InvoiceList;
